import type { Channel, Customer, OrderChannel } from './types';

/** How often and how recently a mock customer orders. */
export type OrderPattern = 'new' | 'regular' | 'vip' | 'at_risk' | 'lapsed' | 'occasional';

/**
 * A hand-written customer. Orders, spend, visits and consent history are generated
 * from `seed` in mockCustomers.ts so they stay recent whenever the app is opened.
 */
export type NamedCustomer =
  Pick<Customer, 'id' | 'name' | 'tags' | 'riskStatus' | 'originChannel' | 'auditLog' | 'createdAt'>
  & Partial<Pick<Customer,
    | 'phone' | 'email' | 'birthday' | 'notes' | 'activeFlag' | 'lifecycleState' | 'preferences' | 'dietaryNotes'
    | 'allergyNotes' | 'importantDates' | 'loyaltyStatus' | 'favouriteItems' | 'customAttributes' | 'groupIds' | 'savedCards'>>
  & { seed: { pattern: OrderPattern; optIn: Channel[]; app?: boolean; orderChannel?: OrderChannel } };

export const namedCustomers: NamedCustomer[] = [
  {
    id: 'cus-1001',
    name: 'John Doe',
    phone: '+1 415 555 1234',
    email: 'john.doe@example.com',
    birthday: '1988-03-14',
    notes: 'Regular at the outlet. Prefers window seating.',
    tags: ['VIP', 'Regular'],
    riskStatus: 'none',
    originChannel: 'loyalty',
    preferences: 'Window seating, still water, no music near table.',
    dietaryNotes: 'Pescatarian.',
    allergyNotes: '',
    favouriteItems: ['Grilled Salmon', 'Caesar Salad', 'Espresso'],
    importantDates: [{ id: 'd1', label: 'Anniversary', date: '2015-09-12' }],
    loyaltyStatus: { tier: 'Gold', points: 4280, memberSince: '2024-11-02' },
    customAttributes: [{ id: 'a1', key: 'Preferred server', value: 'Marco' }],
    groupIds: ['grp-vip'],
    savedCards: [
      { id: 'card-1', brand: 'Visa', last4: '4242', expMonth: 8, expYear: 2028, isDefault: true, addedAt: '2025-03-01T10:00:00Z' },
      { id: 'card-2', brand: 'Mastercard', last4: '5512', expMonth: 2, expYear: 2027, isDefault: false, addedAt: '2025-09-12T14:00:00Z' },
    ],
    auditLog: [],
    createdAt: '2024-11-02T09:30:00Z',
    seed: { pattern: 'vip', optIn: ['email', 'sms'] },
  },
  {
    id: 'cus-1002',
    name: 'Marcus Bell',
    phone: '+1 415 555 8890',
    email: 'm.bell@example.com',
    birthday: '1979-09-01',
    notes: 'Three chargebacks disputed in Q1. Watch closely.',
    tags: ['Disputed'],
    riskStatus: 'blocked',
    activeFlag: {
      status: 'blocked',
      reasonCode: 'chargeback_abuse',
      note: 'Filed chargebacks on 3 completed dine-in checks despite signed receipts.',
      setBy: 'Aisha Khan',
      setAt: '2026-05-28T16:10:00Z',
    },
    originChannel: 'pos',
    auditLog: [
      { id: 'aud-1', action: 'set', fromStatus: 'none', toStatus: 'watch', reasonCode: 'chargeback_abuse', note: 'First disputed chargeback flagged for review.', actor: 'Aisha Khan', timestamp: '2026-04-12T14:02:00Z' },
      { id: 'aud-2', action: 'changed', fromStatus: 'watch', toStatus: 'blocked', reasonCode: 'chargeback_abuse', note: 'Escalated to Blocked after third chargeback.', actor: 'Aisha Khan', timestamp: '2026-05-28T16:10:00Z' },
    ],
    createdAt: '2025-08-19T11:00:00Z',
    seed: { pattern: 'occasional', optIn: ['email'] },
  },
  {
    id: 'cus-1003',
    name: 'Priya Nair',
    phone: '+1 408 555 7781',
    email: 'priya.nair@example.com',
    birthday: '1992-12-22',
    notes: '',
    tags: ['Regular'],
    riskStatus: 'watch',
    activeFlag: { status: 'watch', reasonCode: 'no_show', note: 'Two no-shows on large reservations in May.', setBy: 'Marco Diaz', setAt: '2026-06-01T10:30:00Z' },
    originChannel: 'reservation',
    dietaryNotes: 'Vegetarian.',
    favouriteItems: ['Paneer Tikka', 'Mango Lassi'],
    loyaltyStatus: { tier: 'Silver', points: 1320, memberSince: '2025-02-11' },
    auditLog: [{ id: 'aud-3', action: 'set', fromStatus: 'none', toStatus: 'watch', reasonCode: 'no_show', note: 'Two no-shows on large reservations in May.', actor: 'Marco Diaz', timestamp: '2026-06-01T10:30:00Z' }],
    createdAt: '2025-02-11T08:45:00Z',
    seed: { pattern: 'regular', optIn: ['email'] },
  },
  {
    id: 'cus-1004',
    name: 'Sofia Rossi',
    phone: '+1 510 555 3344',
    email: 'sofia.rossi@example.com',
    birthday: '1985-06-30',
    notes: 'Catering contact for Rossi & Co.',
    tags: ['Catering', 'VIP'],
    riskStatus: 'none',
    originChannel: 'app',
    preferences: 'Invoices to accounts@rossico.example. Large orders need 48h notice.',
    allergyNotes: 'Severe nut allergy — flag all catering orders.',
    favouriteItems: ['Catering Platter A', 'Tiramisu'],
    importantDates: [{ id: 'd2', label: 'Company founding', date: '2010-04-01' }],
    loyaltyStatus: { tier: 'Platinum', points: 9120, memberSince: '2023-05-30' },
    groupIds: ['grp-vip', 'grp-cater'],
    savedCards: [{ id: 'card-3', brand: 'Amex', last4: '0005', expMonth: 11, expYear: 2026, isDefault: true, addedAt: '2024-01-15T09:00:00Z' }],
    auditLog: [],
    createdAt: '2023-05-30T15:20:00Z',
    seed: { pattern: 'vip', optIn: ['email', 'sms', 'push'], app: true, orderChannel: 'catering' },
  },
  {
    id: 'cus-1005',
    name: 'Daniel Osei',
    phone: '+1 415 555 6677',
    email: '',
    birthday: '',
    notes: 'Promo code stacking detected on app orders.',
    tags: [],
    riskStatus: 'blocked',
    activeFlag: { status: 'blocked', reasonCode: 'promo_abuse', note: 'Created multiple accounts to reuse first-order promo.', setBy: 'Aisha Khan', setAt: '2026-06-05T09:15:00Z' },
    originChannel: 'app',
    auditLog: [{ id: 'aud-4', action: 'set', fromStatus: 'none', toStatus: 'blocked', reasonCode: 'promo_abuse', note: 'Created multiple accounts to reuse first-order promo.', actor: 'Aisha Khan', timestamp: '2026-06-05T09:15:00Z' }],
    createdAt: '2026-03-22T17:00:00Z',
    seed: { pattern: 'occasional', optIn: ['push'], app: true },
  },
  {
    id: 'cus-1006',
    name: 'Emily Carter',
    phone: '+1 650 555 1212',
    email: 'emily.carter@example.com',
    birthday: '1990-01-08',
    notes: '',
    tags: ['Regular'],
    riskStatus: 'none',
    originChannel: 'online_qr',
    favouriteItems: ['Margherita Pizza'],
    auditLog: [],
    createdAt: '2025-09-14T10:10:00Z',
    seed: { pattern: 'at_risk', optIn: ['email'] },
  },
  {
    id: 'cus-1007',
    name: 'Ravi Patel',
    phone: '+1 408 555 9090',
    email: 'ravi.patel@example.com',
    birthday: '1983-07-19',
    notes: 'Comp/refund pattern under review.',
    tags: ['Disputed'],
    riskStatus: 'watch',
    activeFlag: { status: 'watch', reasonCode: 'refund_comp_abuse', note: 'Requested comps on 4 of last 5 visits citing minor issues.', setBy: 'Marco Diaz', setAt: '2026-06-12T15:40:00Z' },
    originChannel: 'pos',
    auditLog: [{ id: 'aud-5', action: 'set', fromStatus: 'none', toStatus: 'watch', reasonCode: 'refund_comp_abuse', note: 'Requested comps on 4 of last 5 visits citing minor issues.', actor: 'Marco Diaz', timestamp: '2026-06-12T15:40:00Z' }],
    createdAt: '2024-12-01T12:00:00Z',
    seed: { pattern: 'regular', optIn: [] },
  },
  {
    id: 'cus-1008',
    name: 'Grace Liu',
    phone: '',
    email: 'grace.liu@example.com',
    birthday: '1995-11-03',
    notes: 'Email-only record from app signup.',
    tags: [],
    riskStatus: 'none',
    originChannel: 'app',
    auditLog: [],
    createdAt: '2026-01-18T09:00:00Z',
    seed: { pattern: 'occasional', optIn: ['email', 'sms', 'push'], app: true },
  },
  {
    id: 'cus-1009',
    name: 'Thomas Brown',
    phone: '+1 415 555 4455',
    email: 'tbrown@example.com',
    birthday: '1976-02-25',
    notes: 'Previously flagged, cleared after appeal.',
    tags: ['Regular'],
    riskStatus: 'none',
    originChannel: 'back_office_manual',
    auditLog: [
      { id: 'aud-6', action: 'set', fromStatus: 'none', toStatus: 'watch', reasonCode: 'behavioural', note: 'Reported dispute with staff.', actor: 'Aisha Khan', timestamp: '2026-02-09T13:00:00Z' },
      { id: 'aud-7', action: 'cleared', fromStatus: 'watch', toStatus: 'none', note: 'Misunderstanding resolved; customer apologised. Cleared on appeal.', actor: 'Owner — Elena Park', timestamp: '2026-03-01T11:30:00Z' },
    ],
    createdAt: '2024-07-07T16:40:00Z',
    seed: { pattern: 'regular', optIn: [] },
  },
  {
    id: 'cus-1010',
    name: 'Nadia Hassan',
    phone: '+1 510 555 2323',
    email: 'nadia.h@example.com',
    birthday: '1998-04-17',
    notes: '',
    tags: ['New'],
    riskStatus: 'none',
    originChannel: 'online_qr',
    auditLog: [],
    createdAt: '2026-06-01T10:00:00Z',
    seed: { pattern: 'new', optIn: ['email'] },
  },
  {
    id: 'cus-1011',
    name: 'Oliver Smith',
    phone: '+1 650 555 7878',
    email: 'oliver.smith@example.com',
    birthday: '1981-10-12',
    notes: 'Has not visited in months.',
    tags: [],
    riskStatus: 'none',
    originChannel: 'pos',
    lifecycleState: 'inactive',
    auditLog: [],
    createdAt: '2025-06-23T14:00:00Z',
    seed: { pattern: 'lapsed', optIn: [] },
  },
  {
    id: 'cus-1012',
    name: 'Mei Tanaka',
    phone: '+1 415 555 9911',
    email: 'mei.tanaka@example.com',
    birthday: '1993-08-05',
    notes: 'VIP — anniversary dinner each May.',
    tags: ['VIP'],
    riskStatus: 'none',
    originChannel: 'loyalty',
    preferences: 'Quiet corner table. Celebrates anniversary in May.',
    favouriteItems: ['Omakase', 'Sake flight'],
    importantDates: [{ id: 'd3', label: 'Anniversary', date: '2016-05-20' }],
    loyaltyStatus: { tier: 'Gold', points: 5610, memberSince: '2023-01-15' },
    groupIds: ['grp-vip'],
    auditLog: [],
    createdAt: '2023-01-15T18:00:00Z',
    seed: { pattern: 'vip', optIn: ['email', 'sms'] },
  },
  {
    id: 'cus-1013',
    name: 'Henry Ford',
    phone: '+1 415 555 0001',
    email: 'henry.ford@example.com',
    birthday: '1970-05-22',
    notes: 'Archived — moved away.',
    tags: [],
    riskStatus: 'none',
    originChannel: 'pos',
    lifecycleState: 'archived',
    auditLog: [],
    createdAt: '2024-02-10T12:00:00Z',
    seed: { pattern: 'lapsed', optIn: [] },
  },
];
